import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

export type SignInOutcome = 'ok' | 'new-password-required';

interface StoredTokens {
    accessToken: string;
    idToken: string;
    refreshToken: string | null;
    /** Epoch ms after which the access token is stale. */
    expiresAt: number;
}

interface TokensResponse {
    accessToken: string;
    idToken: string;
    refreshToken: string | null;
    expiresIn: number;
}

const STORAGE_KEY = 'epi.auth.v1';
/** PKCE state for an in-flight Google redirect. sessionStorage: this tab, this attempt. */
const PKCE_KEY = 'epi.auth.pkce';
/** Set while a post-adoption automatic redo is in flight — allows exactly one. */
const RELINK_KEY = 'epi.auth.relink';

// Sign-in AGAINST OUR API ("the idea would be login against our api right?", 2026-08-17): the
// browser posts credentials to /central/auth/login, the API runs the Cognito exchange server-side
// on the same user pool + client the hosted UI used, and the SAME JWTs come back — same users,
// same passwords, no amazoncognito.com page or SDK in the browser at all. The rest of the app
// is unchanged in shape: the access token rides the Authorization header exactly as the OIDC
// interceptor sent it, and refresh happens through /central/auth/refresh when it goes stale.
@Injectable({ providedIn: 'root' })
export class AuthService {
    private readonly http = inject(HttpClient);
    private readonly base = `${environment.apiBaseUrl}/central/auth`;

    private tokens: StoredTokens | null = null;
    /** Single-flight: many parallel API calls at expiry must produce ONE refresh request. */
    private refreshing: Promise<boolean> | null = null;
    /** NEW_PASSWORD_REQUIRED continuation (admin-created users' first login). */
    private challenge: { username: string; session: string; requiredAttributes: string[] } | null = null;

    readonly isAuthenticated = signal(false);
    /** ID-token claims: email, cognito:username, cognito:groups, sub, … */
    readonly claims = signal<Record<string, unknown> | null>(null);

    /**
     * A Google redirect landing on this page, being exchanged. Cognito sends the code back to the
     * SPA's ORIGIN — the only redirect URI the app client has registered — so it arrives at the
     * root route, before any component asks about the session. Started here and awaited by
     * `ensureSession()`, so the route guard cannot bounce a returning visitor to the login page
     * while their own sign-in is still completing.
     */
    private bootstrap: Promise<void> | null = null;
    /** Where the visitor was headed before Google, carried through the redirect in `state`. */
    private pendingReturnUrl: string | null = null;

    constructor() {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) this.adopt(JSON.parse(raw) as StoredTokens, false);
        } catch {
            // Unreadable storage — start signed out.
        }
        // A MICROTASK, not a direct call: consumeRedirect posts through HttpClient, whose
        // interceptors inject THIS service — synchronously from inside the constructor that is a
        // circular dependency (NG0200) that silently killed every Google exchange. Deferring one
        // tick lets construction finish before the first request can possibly fire.
        this.bootstrap = Promise.resolve().then(() => this.consumeRedirect());
    }

    /** Set once by a completed Google redirect; the login page reads it to finish navigating. */
    takeReturnUrl(): string | null {
        const url = this.pendingReturnUrl;
        this.pendingReturnUrl = null;
        return url;
    }

    /**
     * Send the browser to Google. `identity_provider=Google` is what keeps Cognito from drawing its
     * own login form: it redirects straight to Google's account chooser instead.
     */
    async startGoogleSignIn(returnUrl?: string | null): Promise<void> {
        const verifier = randomUrlSafe(64);
        const pkce: Pkce = {
            verifier,
            state: randomUrlSafe(16),
            challenge: await s256(verifier),
            returnUrl: returnUrl ?? null,
            phase: 'logout',
            startedAt: Date.now()
        };
        sessionStorage.setItem(PKCE_KEY, JSON.stringify(pkce));

        // CLEAR COGNITO'S OWN SESSION FIRST, on EVERY attempt — not only after a failure.
        //
        // Cognito keeps a session cookie of its own on the hosted domain. Once someone has signed
        // in through it, a later /authorize is answered from that cookie: Google is never asked
        // again, so the second person (or the same person with a different account) is silently
        // handed the FIRST identity. There is no visible error and no way out from inside the app
        // — the account picker simply never appears again.
        //
        // Going through /logout costs one extra redirect and makes every sign-in start from a
        // known-empty state. `prompt=login` would be the OIDC way to say this, but Cognito does not
        // honour it for federated providers.
        const c = environment.cognito;
        window.location.assign(
            `${c.cognitoDomain}/logout` +
            `?client_id=${encodeURIComponent(c.clientId)}` +
            `&logout_uri=${encodeURIComponent(redirectUri())}`
        );
    }

    /**
     * The second leg: Cognito has just dropped its session and bounced us home. Continue to Google.
     * The marker is the stored PKCE itself, NOT a query parameter — `logout_uri` has to match a
     * registered Sign-out URL byte for byte, so nothing can be appended to it.
     */
    private goToGoogle(pkce: Pkce): void {
        sessionStorage.setItem(PKCE_KEY, JSON.stringify({ ...pkce, phase: 'authorize' }));

        const c = environment.cognito;
        window.location.assign(
            `${c.cognitoDomain}/oauth2/authorize` +
            `?identity_provider=Google` +
            `&response_type=code` +
            `&client_id=${encodeURIComponent(c.clientId)}` +
            `&redirect_uri=${encodeURIComponent(redirectUri())}` +
            `&scope=${encodeURIComponent(c.scope)}` +
            `&state=${encodeURIComponent(pkce.state)}` +
            `&code_challenge=${encodeURIComponent(pkce.challenge)}` +
            `&code_challenge_method=S256`
        );
    }

    /**
     * Finish a Google sign-in if this page load is one. Silent when it is not — every page load
     * runs this. Anything that fails leaves the visitor signed out on the login page, which is the
     * correct outcome for a sign-in that did not complete.
     */
    private async consumeRedirect(): Promise<void> {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');
        const oauthError = params.get('error');

        const stored = readPkce();

        // Coming back from the session-clearing hop: no code yet, this is the halfway point.
        if (!code && !oauthError && stored?.phase === 'logout') {
            this.goToGoogle(stored);
            // Never resolves in practice — the navigation is already underway, and resolving would
            // let the guard run and redraw the login page underneath the departing browser.
            return new Promise<void>(() => {});
        }

        if (!code && !oauthError) return;

        const rawPkce = sessionStorage.getItem(PKCE_KEY);
        sessionStorage.removeItem(PKCE_KEY);
        // The code is single-use and now spent: strip it so a refresh cannot replay a dead code
        // and so it never reaches browser history or a referrer header.
        stripOauthParams();

        if (oauthError) {
            this.redirectError = params.get('error_description') || oauthError;
            return;
        }

        if (!rawPkce) {
            this.redirectError = 'The sign-in could not be matched to this browser tab — try again.';
            return;
        }

        let pkce: Pkce;
        try {
            pkce = JSON.parse(rawPkce);
        } catch {
            this.redirectError = 'The sign-in could not be completed — try again.';
            return;
        }

        // CSRF: the value that came back must be the one this tab generated.
        if (!state || state !== pkce.state) {
            this.redirectError = 'The sign-in response did not match this request — try again.';
            return;
        }

        try {
            const resp = await firstValueFrom(
                this.http.post<TokensResponse & { linked?: boolean }>(`${this.base}/google/exchange`, {
                    code,
                    codeVerifier: pkce.verifier,
                    // Must be byte-identical to the one sent to /authorize — Cognito re-checks it.
                    redirectUri: redirectUri()
                })
            );
            if (resp.linked) {
                // First-ever Google sign-in for a known email: the API just absorbed the freshly
                // created twin into the native account. Redo the sign-in — it rides Google's warm
                // session and lands in the real account. Guarded to ONE redo per attempt chain, so
                // an adoption that somehow repeats cannot ping-pong the browser.
                if (sessionStorage.getItem(RELINK_KEY)) {
                    sessionStorage.removeItem(RELINK_KEY);
                    this.redirectError = 'The account was linked but the sign-in kept restarting — try once more.';
                    return;
                }
                sessionStorage.setItem(RELINK_KEY, '1');
                await this.startGoogleSignIn(pkce.returnUrl);
                return new Promise<void>(() => {}); // navigation is underway
            }
            sessionStorage.removeItem(RELINK_KEY);
            this.adoptResponse(resp);
            this.pendingReturnUrl = pkce.returnUrl;
        } catch (err) {
            // The full error goes to the console — an exchange can fail for reasons (a DI quirk,
            // a thrown interceptor) that produce no HTTP response at all, and those must not be
            // invisible behind a friendly line.
            console.error('[auth] Google code exchange failed', err);
            const apiMessage = (err as { error?: { error?: string } } | null)?.error?.error;
            const status = (err as { status?: number } | null)?.status;
            this.redirectError = apiMessage
                ?? (status === 0
                    ? 'The API is not reachable — is the middleware running?'
                    : 'Google sign-in did not complete — try again.');
        }
    }

    /** Why a Google redirect failed, for the login page to show. Read once. */
    private redirectError: string | null = null;

    takeRedirectError(): string | null {
        const message = this.redirectError;
        this.redirectError = null;
        return message;
    }

    async signIn(username: string, password: string): Promise<SignInOutcome> {
        const resp = await firstValueFrom(
            this.http.post<TokensResponse & { challenge?: string; session?: string; requiredAttributes?: string[] }>(`${this.base}/login`, {
                username: username.trim(),
                password
            })
        );
        if (resp.challenge === 'NEW_PASSWORD_REQUIRED' && resp.session) {
            this.challenge = {
                username: username.trim(),
                session: resp.session,
                requiredAttributes: resp.requiredAttributes ?? []
            };
            return 'new-password-required';
        }
        this.adoptResponse(resp);
        return 'ok';
    }

    /** Pool-required attributes the pending challenge must supply — the form renders one field each. */
    challengeAttributes(): string[] {
        return this.challenge?.requiredAttributes ?? [];
    }

    async completeNewPassword(newPassword: string, attributes?: Record<string, string>): Promise<void> {
        const c = this.challenge;
        if (!c) throw new Error('No sign-in is waiting for a new password.');
        const resp = await firstValueFrom(
            this.http.post<TokensResponse>(`${this.base}/new-password`, {
                username: c.username,
                newPassword,
                session: c.session,
                attributes: attributes ?? {}
            })
        );
        this.challenge = null;
        this.adoptResponse(resp);
    }

    /** The token API requests carry. Refreshes through our API when stale; null = signed out. */
    async getAccessToken(): Promise<string | null> {
        if (!this.tokens) return null;
        if (Date.now() < this.tokens.expiresAt - 60_000) return this.tokens.accessToken;
        return (await this.refresh()) ? this.tokens!.accessToken : null;
    }

    /** True when a session exists or could be refreshed — the route guard's question. */
    async ensureSession(): Promise<boolean> {
        // A Google redirect in flight has to settle first, or the guard decides "signed out" about
        // a visitor who is three hundred milliseconds from being signed in.
        if (this.bootstrap) {
            await this.bootstrap;
            this.bootstrap = null;
        }
        return (await this.getAccessToken()) !== null;
    }

    signOut(): void {
        this.tokens = null;
        this.challenge = null;
        this.isAuthenticated.set(false);
        this.claims.set(null);
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch {
            // ignored
        }
    }

    private refresh(): Promise<boolean> {
        this.refreshing ??= (async () => {
            try {
                const refreshToken = this.tokens?.refreshToken;
                if (!refreshToken) return false;
                const resp = await firstValueFrom(
                    this.http.post<TokensResponse>(`${this.base}/refresh`, { refreshToken })
                );
                // Cognito does not rotate the refresh token on this flow — keep the one we hold.
                this.adoptResponse({ ...resp, refreshToken: resp.refreshToken ?? refreshToken });
                return true;
            } catch {
                this.signOut();
                return false;
            } finally {
                this.refreshing = null;
            }
        })();
        return this.refreshing;
    }

    private adoptResponse(resp: TokensResponse): void {
        this.adopt(
            {
                accessToken: resp.accessToken,
                idToken: resp.idToken,
                refreshToken: resp.refreshToken,
                expiresAt: Date.now() + resp.expiresIn * 1000
            },
            true
        );
    }

    private adopt(tokens: StoredTokens, persist: boolean): void {
        this.tokens = tokens;
        this.claims.set(decodeJwtPayload(tokens.idToken));
        this.isAuthenticated.set(true);
        if (persist) {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
            } catch {
                // Session survives this tab only — acceptable degradation.
            }
        }
    }
}

interface Pkce {
    verifier: string;
    state: string;
    challenge: string;
    returnUrl: string | null;
    /** 'logout' = Cognito's session is being cleared; 'authorize' = away at Google. */
    phase: 'logout' | 'authorize';
    startedAt: number;
}

/** How long a half-finished sign-in stays actionable. */
const PKCE_TTL_MS = 5 * 60_000;

/**
 * The stored sign-in attempt, if it is still live. Expiry matters: the marker for "continue to
 * Google" is the stored value itself, so a blob left behind by an abandoned attempt would hijack an
 * ordinary page load into a redirect the visitor never asked for.
 */
function readPkce(): Pkce | null {
    const raw = sessionStorage.getItem(PKCE_KEY);
    if (!raw) return null;
    try {
        const pkce = JSON.parse(raw) as Pkce;
        if (Date.now() - pkce.startedAt > PKCE_TTL_MS) {
            sessionStorage.removeItem(PKCE_KEY);
            return null;
        }
        return pkce;
    } catch {
        sessionStorage.removeItem(PKCE_KEY);
        return null;
    }
}

/**
 * Where Cognito sends the browser back — THE ORIGIN THIS APP IS ACTUALLY SERVED FROM, read at
 * runtime rather than taken from the environment file.
 *
 * The admin is served from several hosts (central.dev / central.qa / central.liquorcart.com) off
 * builds whose `environment.*.ts` still said `http://localhost:4222`. A hardcoded value that does
 * not match the current host does not fail loudly: Cognito accepts the sign-in and then sends the
 * person to localhost, which works perfectly on the developer's machine and strands everyone else.
 * Deriving it removes the chance of that drift — one build is correct on every host, provided each
 * origin is listed as a Callback URL on the app client.
 */
function redirectUri(): string {
    return window.location.origin;
}

function randomUrlSafe(bytes: number): string {
    const buf = new Uint8Array(bytes);
    crypto.getRandomValues(buf);
    return base64Url(buf);
}

async function s256(verifier: string): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    return base64Url(new Uint8Array(digest));
}

function base64Url(bytes: Uint8Array): string {
    return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Drop the OAuth params without a navigation, so Angular's router never sees them. */
function stripOauthParams(): void {
    const url = new URL(window.location.href);
    ['code', 'state', 'error', 'error_description'].forEach((p) => url.searchParams.delete(p));
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
}

function decodeJwtPayload(jwt: string): Record<string, unknown> | null {
    try {
        const payload = jwt.split('.')[1];
        return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
        return null;
    }
}
